import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FunnelSimple, ClipboardText, CheckCircle, Lightning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { JOB_STATUS_ORDER, JOB_STATUS_LABELS, JOB_STATUS_COLORS, LEGACY_JOB_STATUS, inr } from "@/lib/constants";
import { PageHeader, Stat, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

const normStatus = (s) => (JOB_STATUS_ORDER.includes(s) ? s : LEGACY_JOB_STATUS[s] || "enquiry");

export default function PipelineDashboard() {
  const [jobs, setJobs] = useState(null);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => setJobs([]));
  }, []);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(JOB_STATUS_ORDER.map((s) => [s, []]));
    (jobs || []).forEach((j) => { map[normStatus(j.status)].push(j); });
    return map;
  }, [jobs]);

  if (jobs === null) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading pipeline…</div>;

  const total = jobs.length;
  const completed = byStage.completed.length;
  const active = total - completed;
  const selectedJobs = selected ? byStage[selected] : [];

  return (
    <div className="space-y-6" data-testid="pipeline-dashboard-page">
      <PageHeader overline="02 · Valuation Data" title="Pipeline Dashboard" subtitle="Live view of how many jobs sit at each stage of the 13-step workflow" />

      <div className="grid grid-cols-3 gap-4">
        <Stat testid="pipeline-total" label="Total Jobs" value={total} />
        <Stat testid="pipeline-active" label="Active" value={active} tone="text-blue-600" sub="in progress" />
        <Stat testid="pipeline-completed" label="Completed" value={completed} tone="text-emerald-600" sub="released & closed" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {JOB_STATUS_ORDER.map((s, i) => {
          const list = byStage[s];
          const feeSum = list.reduce((a, j) => a + (Number(j.fee) || 0), 0);
          const isSel = selected === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setSelected(isSel ? null : s)}
              data-testid={`pipeline-stage-${s}`}
              className={`text-left rounded-lg border bg-white p-4 transition-all duration-150 hover:-translate-y-0.5 hover:shadow stagger-in ${isSel ? "ring-2 ring-accent border-accent" : "border-border"}`}
              style={{ animationDelay: `${i * 30}ms` }}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${JOB_STATUS_COLORS[s]}`}>{JOB_STATUS_LABELS[s]}</span>
              </div>
              <div className="mt-3 font-display font-black text-3xl text-primary tabular" data-testid={`pipeline-count-${s}`}>{list.length}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{list.length === 1 ? "job" : "jobs"}{feeSum > 0 && <> · {inr(feeSum)}</>}</div>
            </button>
          );
        })}
      </div>

      {selected && (
        <Card className="border-border overflow-hidden" data-testid="pipeline-stage-jobs">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <FunnelSimple size={18} weight="duotone" className="text-accent" />
            <h3 className="font-display text-lg font-semibold text-primary">{JOB_STATUS_LABELS[selected]}</h3>
            <span className="text-xs text-muted-foreground">· {selectedJobs.length} {selectedJobs.length === 1 ? "job" : "jobs"}</span>
          </div>
          {selectedJobs.length === 0 ? (
            <div className="p-5"><EmptyState icon={ClipboardText}>No jobs at this stage.</EmptyState></div>
          ) : (
            <div className="divide-y divide-border">
              {selectedJobs.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => navigate(`/app/jobs?job=${j.id}`)}
                  data-testid={`pipeline-job-${j.id}`}
                  className="w-full text-left px-5 py-3 flex items-center justify-between gap-4 hover:bg-muted/40 transition-colors duration-150"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-primary truncate">{j.applicant_name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">{j.reference}{j.valuation_details?.val_id ? ` · ${j.valuation_details.val_id}` : ""} · {j.bank_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm tabular text-primary">{inr(j.fee)}</div>
                    <div className="text-[10px] text-muted-foreground">{j.deadline ? j.deadline.slice(0, 10) : "no deadline"}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {!selected && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Lightning size={13} weight="fill" className="text-accent" /> Tap any stage card to see the jobs sitting there.</p>
      )}
    </div>
  );
}
